import React, { FC } from "react";
import { observer } from "mobx-react";
import ConfirmedIcon from "../../../../../images/common/check-fill-green.svg";
import styled from "styled-components";
import {
  CheckOutAfterPayModel,
  PurchaseCheckoutItemsDoneModel,
} from "../models";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  Regex,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { CheckOutRedirectOption, CheckOutRedirectTypes } from "../enums";
import RenderPaymentMethod from "./RenderPaymentMethod";
import CarouselWithThumbnail from "./CarousalWithThumbnail";
import { AccountSettingsStore } from "../store";
import visaCard from "../../../../../images/payment/visa-card_logo.svg";

type IProps = {
  afterPayDetails: CheckOutAfterPayModel;
  purchaseDoneResponse: PurchaseCheckoutItemsDoneModel;
  buttonBgColor?: string;
  isPreview?: boolean;
};

export const PurchaseSuccessWrapper = styled(FlexContainer)`
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 30px;
  .purchase-data {
    > div > ${FlexContainer} {
      > ${CustomLabel}, > div {
        color: #555;
        &:first-child {
          width: 200px;
          text-align: left;
        }
        &:nth-child(2) {
          width: 350px;
          text-align: right;
          word-break: break-word;
          ${FlexContainer} {
            justify-content: flex-end;
          }
        }
      }
    }
  }
  .purchase-icons {
    align-items: center;
    justify-content: center;
    gap: 40px;
    margin: 25px 0px 35px;
    .invoice-icon {
      height: 60px;
      width: 50px;
    }
    .download-icon {
      height: 25px;
      width: 24px;
      margin-right: 15px;
    }
  }
  .static-purchase-data {
    label,
    div {
      &:first-child {
        width: 135px !important;
      }
      &:nth-child(2) {
        width: 185px !important;
      }
    }
  }

  @media (max-width: 500px) {
    .purchase-data {
      > div {
        width: 100%;

        > ${FlexContainer} {
          > ${CustomLabel}, > div {
            &:first-child {
              width: 150px;
              text-align: left;
            }
            &:nth-child(2) {
              width: calc(100% - 150px);
              text-align: left;
              word-break: break-word;
              ${FlexContainer} {
                justify-content: flex-start;
              }
            }
          }
        }
      }
    }
  }
  @media (min-width: 501px) and (max-width: 850px) {
    .purchase-data {
      > div {
        width: 100%;
        > ${FlexContainer} {
          > ${CustomLabel}, > div {
            &:first-child {
              width: 150px;
              text-align: left;
            }
            &:nth-child(2) {
              width: calc(100% - 150px);
              word-break: break-word;
            }
          }
        }
      }
    }
  }

  @media (min-width: 851px) and (max-width: 1400px) {
    .purchase-data {
      > div {
        width: 100%;
        > ${FlexContainer} {
          > ${CustomLabel}, > div {
            &:first-child {
              width: 165px;
              text-align: left;
            }
            &:nth-child(2) {
              width: calc(100% - 165px);
              word-break: break-word;
            }
          }
        }
      }
    }
  }
`;

const PurchaseSuccess: FC<IProps> = ({
  afterPayDetails,
  purchaseDoneResponse,
  buttonBgColor,
  isPreview = false,
}) => {
  if (!afterPayDetails && !purchaseDoneResponse) return null;

  function renderDonationFields() {
    if (!purchaseDoneResponse || !purchaseDoneResponse.isDonation) return null;

    return (
      <>
        <div>
          <FlexContainer spacing={30}>
            <CustomLabel>Donation Frequency</CustomLabel>{" "}
            <div>{purchaseDoneResponse.donationFrequency}</div>
          </FlexContainer>
        </div>
      </>
    );
  }
  const confirmationContent = (
    <PurchaseSuccessWrapper>
      <img src={ConfirmedIcon} />
      <CustomLabel
        fontSize="30px"
        fontWeight={"600"}
        lineHeight="36px"
        color="#000"
        margin="0px 0px 30px"
        textAlign="center"
      >
        {purchaseDoneResponse?.isDonation
          ? "Donation Confirmed!"
          : "  Purchase Confirmed!"}
      </CustomLabel>
      {!isPreview ? (
        <FlexContainer
          fxLayout="column"
          alignItems="center"
          spacing={20}
          className="purchase-data"
        >
          <div>
            <FlexContainer spacing={30}>
              <CustomLabel>Payment ID</CustomLabel>

              <CustomLabel>
                {purchaseDoneResponse?.paymentId || "-"}
              </CustomLabel>
            </FlexContainer>
          </div>
          <div>
            <FlexContainer spacing={30}>
              <CustomLabel>Payment Date</CustomLabel>{" "}
              {purchaseDoneResponse?.paymentDate && (
                <div>
                  {UtilityMethods.formatDateInStringWithTime(
                    purchaseDoneResponse?.paymentDate
                  )}
                </div>
              )}
            </FlexContainer>
          </div>
          <div>
            <FlexContainer spacing={30}>
              <CustomLabel>Payment Method</CustomLabel>{" "}
              {purchaseDoneResponse?.paymentMethod && (
                <div>
                  <RenderPaymentMethod
                    paymentMethod={purchaseDoneResponse?.paymentMethod}
                  />
                </div>
              )}
            </FlexContainer>
          </div>
          {purchaseDoneResponse?.amountPaid !== undefined && (
            <div>
              <FlexContainer spacing={30}>
                <CustomLabel>Amount Paid</CustomLabel>{" "}
                <div>
                  $
                  {AccountSettingsStore.formatAmount(
                    purchaseDoneResponse.amountPaid
                  )}
                </div>
              </FlexContainer>
            </div>
          )}
          {renderDonationFields()}
        </FlexContainer>
      ) : (
        <FlexContainer
          fxLayout="column"
          alignItems="center"
          spacing={20}
          className="purchase-data"
        >
          <div>
            <FlexContainer spacing={30} className="static-purchase-data">
              <CustomLabel>Order No:</CustomLabel>
              <CustomLabel>{"1001"}</CustomLabel>
            </FlexContainer>
          </div>
          <div>
            <FlexContainer spacing={30} className="static-purchase-data">
              <CustomLabel>Order Date:</CustomLabel>{" "}
              <div>{UtilityMethods.formatDateInStringWithTime(new Date())}</div>
            </FlexContainer>
          </div>
          <div>
            <FlexContainer spacing={30} className="static-purchase-data">
              <CustomLabel>Payment Method:</CustomLabel>{" "}
              <div>
                {" "}
                <img src={visaCard} alt="card" className="render-card-icons" />
              </div>
            </FlexContainer>
          </div>
          {purchaseDoneResponse?.amountPaid && (
            <div>
              <FlexContainer spacing={30}>
                <CustomLabel>Amount Paid</CustomLabel>{" "}
                <div>
                  $
                  {AccountSettingsStore.formatAmount(
                    purchaseDoneResponse.amountPaid
                  )}
                </div>
              </FlexContainer>
            </div>
          )}
          {renderDonationFields()}
        </FlexContainer>
      )}
    </PurchaseSuccessWrapper>
  );

  switch (CheckOutRedirectOption[afterPayDetails.type]) {
    case CheckOutRedirectOption.None:
      return confirmationContent;
    case CheckOutRedirectOption.Message:
      return (
        <>
          <div>
            {afterPayDetails?.media && (
              <CarouselWithThumbnail
                media={[afterPayDetails?.media]}
                className="option-carousel-wrapper"
              />
            )}

            <div>
              <FlexContainer
                justifyContent="center"
                fxLayout="column"
                padding="0px 20px"
                className="item-right-panel"
              >
                {afterPayDetails.title !== "" && (
                  <CustomLabel
                    className="item-header text-center full-width"
                    margin="0px 0px 10px"
                    dangerouslySetInnerHTML={{
                      __html: UtilityMethods.getEditorHtmlContent(
                        afterPayDetails.titleEditorRawContent || ""
                      ),
                    }}
                  ></CustomLabel>
                )}

                {afterPayDetails.description !== "" && (
                  <CustomLabel
                    margin="0px 0px 10px"
                    dangerouslySetInnerHTML={{
                      __html: UtilityMethods.getEditorHtmlContent(
                        afterPayDetails.descriptionEditorRawContent || ""
                      ),
                    }}
                  ></CustomLabel>
                )}
                {afterPayDetails.showButton && (
                  <CustomButton
                    bgColor={buttonBgColor || ""}
                    className="purchase-main-btn height-50"
                    primary
                    onClick={() => redirect(afterPayDetails)}
                  >
                    {afterPayDetails.buttonText}
                  </CustomButton>
                )}
              </FlexContainer>
            </div>
          </div>
          {confirmationContent}
        </>
      );
  }

  return redirect(afterPayDetails);
};

function redirect(afterPayDetails: CheckOutAfterPayModel) {
  let redirectUrl = afterPayDetails.redirectValue;
  switch (CheckOutRedirectTypes[afterPayDetails.redirectType]) {
    case CheckOutRedirectTypes.Website:
      redirectUrl = afterPayDetails.redirectValue;
      if (!redirectUrl) return null;
      if (!Regex.httpProtocolRegex.test(redirectUrl)) {
        redirectUrl = "http://" + redirectUrl;
      }
      window.location.href = redirectUrl;
      break;
    case CheckOutRedirectTypes.ClickToEmail:
      if (Regex.validateEmail.test(redirectUrl)) {
        redirectUrl = `mailto:${redirectUrl}`;
      }
      break;
    case CheckOutRedirectTypes.ClickToCall:
      redirectUrl = `tel:${redirectUrl}`;
      break;
    case CheckOutRedirectTypes.ClickToText:
      redirectUrl = `sms:${redirectUrl}`;
      break;
    default:
      return null;
  }

  if (typeof window !== undefined && redirectUrl) {
    // //IF POPUP BLOCKED IN BROWSER THEN OPEN URL IN SAME WINDOW AS PER REQUIREMENT
    // const response = window.open(redirectUrl, '_blank');
    // if (response) {
    //     return;
    // }
    window.open(redirectUrl, "_self");
    return null;
  }
  return null;
}

export default withRouter(observer(PurchaseSuccess));
